import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { Button, Typography } from '@cannect/new-components/atoms'
import { useToggleProductFavorite } from '@cannect/services/resources/products'
import LoginImage from 'assets/img/login/loginImage.png'
import InvisibleButton from 'components/InvisibleButton'
import { useAuth } from 'hooks/AuthContext'
import { useBrand } from 'hooks/useBrand'
import { getPathFrom, removePahtFrom, setPathFrom } from 'hooks/usePathsRedirect'
import 'styles/remederi-theme.css'
import 'styles/vikura-theme.css'
import { usePendingActions } from 'hooks/usePendingActions'
import useSignedUser from 'hooks/useSignedUser'
import { useTokenApi } from 'hooks/useTokenApi'
import Input from 'new-components/Input'
import { FormEvent, useEffect, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import { authenticate } from 'usecases/user'
import { clearSessionCartId, getSessionCartId, markSessionCartResolved } from 'utils/anonymousCart'
import Loading from '../../components/Loading'
import { setUser } from '../../hooks/useSessionUser'
import api, { defaultsHeadersAxios } from '../../services/api'

export default function Login() {
  const { push } = useHistory()
  const { state } = useLocation() as any
  const { brand, config } = useBrand()
  // Whitelabel (Vikura/Remederi via /marca/login): tema + nome da marca no
  // lugar dos verdes/textos Cannect. Cannect segue idêntico.
  const isWhitelabel = brand !== 'cannect'
  const themeClass = isWhitelabel ? (config.scheduling.themeClass ?? '') : ''
  const remindOnColor = isWhitelabel ? 'bg-primary-700' : 'bg-[#6C8640]'
  const { getRoles } = useAuth()
  const { setTokenApi } = useTokenApi()
  const { getPendingAction, removePendingAction } = usePendingActions()
  const { addToCart, setOrderId } = usePrepareCart()
  const { refetchSignedUser } = useSignedUser()
  const toggleFavoriteMutation = useToggleProductFavorite()
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remind, setRemind] = useState(false)

  const utmRegister = `?utm_medium=organic&utm_source=site&utm_campaign=202[…]site_cannect_organic_geral&utm_content=cannect&utm_term=link`

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault()
    try {
      setLoading(true)
      const sessionCartId = getSessionCartId()
      const response = await authenticate({
        email,
        password,
        ...(sessionCartId ? { session_cart_id: sessionCartId } : {})
      })
      if (sessionCartId) {
        markSessionCartResolved(sessionCartId)
      }
      setTokenApi(response.token)
      defaultsHeadersAxios(response.token)
      await setUser(response.user)
      getRoles()

      await refetchSignedUser()

      const isHealthProfessional = response.user.registration_type === 2

      const pendingAction = getPendingAction()
      if (pendingAction) {
        try {
          switch (pendingAction.type) {
            case 'ADD_TO_CART': {
              const { productId, quantity, classId, brandId } = pendingAction.payload
              if (productId && quantity && classId && brandId) {
                addToCart(productId, quantity, classId, brandId)
                toast.success('Produto adicionado ao carrinho!')
              }
              break
            }
            case 'TOGGLE_FAVORITE': {
              if (pendingAction.payload.productId) {
                toggleFavoriteMutation.mutate(pendingAction.payload.productId)
              }
              break
            }
            default:
              break
          }
        } catch (error) {
          // noop
        }
        removePendingAction()
      }

      const pathFrom = await getPathFrom()
      const isInstantConsultationRedirect = pathFrom?.startsWith('/consulta-na-hora')
      const pageToRedirect = isInstantConsultationRedirect
        ? '/consulta-na-hora'
        : isHealthProfessional
          ? '/painel-do-usuario'
          : pathFrom || '/painel-do-usuario'

      await removePahtFrom()

      if (response.order_id) {
        setOrderId(response.order_id)
        clearSessionCartId()
        push('/carrinho')
      } else {
        push(pageToRedirect)
      }

      if (isHealthProfessional) {
        let healthProfessionalToken: string | undefined
        try {
          const cannectToken = localStorage.getItem('@CANNECT_TOKEN_API')
          if (cannectToken) {
            const { data } = await api.post(
              '/generate-assistant-token',
              {},
              {
                headers: {
                  Authorization: `Bearer ${cannectToken}`
                }
              }
            )
            healthProfessionalToken = data?.token
          }
        } catch (err) {
          // noop
        }

        const healthProfessionalBaseUrl =
          import.meta.env.VITE_APP_HEALTH_PROFESSIONAL_BASE_URL || 'http://localhost:3003/'

        if (healthProfessionalToken) {
          window.open(`${healthProfessionalBaseUrl}login?token=${healthProfessionalToken}`)
        } else {
          window.open(`${healthProfessionalBaseUrl}login`)
        }
      }
    } catch (e: any) {
      toast.error('E-mail ou senha incorretos')
    } finally {
      setLoading(false)
    }
  }

  const handleToRegister = () => {
    push({ pathname: `/cadastro`, state: { from: { pathname: state?.from?.pathname } } })
  }

  useEffect(() => {
    if (state && state?.from && state?.from?.pathname) {
      setPathFrom(state?.from?.search ? `${state?.from?.pathname}${state?.from?.search}` : state?.from?.pathname)
    }
  }, [])

  return (
    <div className={`mx-auto h-full w-full rounded-3xl p-0 pb-16 md:mt-4 md:h-[77%] ${themeClass}`.trim()}>
      <Loading loading={loading} />

      <div className="grid h-full grid-cols-1 gap-4 rounded-3xl bg-secondary-100 md:h-[686px] md:grid-cols-2 md:gap-6">
        <div className="relative h-full overflow-hidden rounded-l-3xl rounded-r-lg bg-secondary-100">
          <img src={LoginImage} alt="Paciente feliz" className="absolute inset-0 h-full w-full object-cover" />
          <div className="pointer-events-none absolute inset-0 bg-black/20" />

          <div className="absolute bottom-6 left-6 right-6 text-white">
            <p className="mb-4 font-ebGaramond text-xl font-semibold leading-snug md:text-[24px]">
              “Fiquei impressionada com a
              <br /> rapidez do agendamento.
              <br /> Marquei minha consulta em
              <br /> minutos e me senti acolhida.”
            </p>
            <div>
              <p className="font-semibold text-primary-200">Clarice Oliveira</p>
              <p className="text-sm text-primary-200 opacity-90">Paciente de fibromialgia</p>
            </div>
          </div>
        </div>

        <div className="h-full rounded-xl bg-[#EDECE3] p-6 md:p-12">
          <Typography.Heading type="headingTwo" weight="semibold" className="mb-2 text-neutral-800 md:text-2xl">
            Bem vindo de volta!
          </Typography.Heading>

          <form onSubmit={handleLogin} className="mt-8 flex flex-col gap-3">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-semibold text-neutral-500">
                E-mail ou CPF
              </span>
              <Input
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                placeholder=""
                className="h-12 w-full appearance-none rounded-lg bg-white pl-32 pr-4 text-neutral-800 md:pl-36 [&:-webkit-autofill]:text-neutral-800 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#ffffff] [&:-webkit-autofill]:[transition:background-color_5000s_ease-in-out_0s]"
              />
            </div>

            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-semibold text-neutral-500">
                Senha
              </span>
              <Input
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                type="password"
                className="h-12 w-full appearance-none rounded-lg bg-white pl-16 pr-12 text-neutral-800 md:pl-20 [&:-webkit-autofill]:text-neutral-800 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#ffffff] [&:-webkit-autofill]:[transition:background-color_5000s_ease-in-out_0s]"
              />
            </div>

            <div className="-mt-1 flex items-center justify-between">
              <InvisibleButton
                className="text-sm text-primary-800 hover:underline"
                type="button"
                color="#6b7b3c"
                style={{ textDecoration: 'none' }}
                onClick={() => push('/esqueci-minha-senha')}>
                Esqueci minha senha
              </InvisibleButton>

              <button
                type="button"
                onClick={() => setRemind((s) => !s)}
                className="group inline-flex items-center gap-2 border-0 bg-transparent p-0 text-sm text-primary-800"
                aria-pressed={remind}>
                <span
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
                    remind ? remindOnColor : 'bg-neutral-300'
                  }`}>
                  <span
                    className={`absolute left-0.5 top-0.5 inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition ${
                      remind ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </span>
                <span className="whitespace-nowrap">Lembrar de mim</span>
              </button>
            </div>

            <ReCAPTCHA sitekey="6LfI-rcjAAAAAGtDe1_H4sxhfEXy-gjGIXWY_VL_" size="invisible" badge="bottomleft" />

            <Button
              type="submit"
              className={`h-12 w-full rounded-lg text-white ${
                isWhitelabel ? 'bg-primary-700 hover:bg-primary-800' : 'bg-[#6C8640] hover:bg-[#5a7337]'
              }`}>
              {isWhitelabel ? `Entrar na ${config.name}` : 'Entrar na Cannect'}
            </Button>

            <p className="text-center text-sm text-neutral-500">
              Ainda não possui uma conta?{' '}
              <button
                type="button"
                onClick={handleToRegister}
                className="border-0 font-semibold text-information-300 hover:underline">
                Crie aqui
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
